import {Injectable} from '@angular/core';
import {PAYMENT_TYPE, PRODUCT_TYPE, USER_EVENT_TYPE} from '../constants';
import {environment} from '../../../environments/environment';
import {AuthService} from './auth.service';
import {HttpClient} from '@angular/common/http';
import {of} from 'rxjs';
import {UtilitiesService} from './utilities.service';
import {Angulartics2} from 'angulartics2';
import {Router} from '@angular/router';
import {NewuserService} from './newuser.service';
import * as moment from "moment";

@Injectable({
  providedIn: 'root'
})
export class EventTrackingService {

    private _userDetails: any = {};
    private _prospectId: number = 0;
    private readonly _persistentKey: string;
    private _sessionKey: any;

    private static readonly DEFAULT_ITEM_LIST_ID = "standard_catalog";
    private static readonly DEFAULT_ITEM_LIST_NAME = "Standard Catalog";

    constructor(private http: HttpClient,
              private authService: AuthService,
              private utilitiesService: UtilitiesService,
              private angulartics2: Angulartics2,
              private newuserService: NewuserService,
              private router: Router) {

        // generate session key if it doesn't exist or if it's expired
        const sessionKeyString = this.utilitiesService.getLocal('sessionKey');
        if (UtilitiesService.isNotEmpty(sessionKeyString)) {
            try {
                this._sessionKey = JSON.parse(sessionKeyString);
            } catch (e) {
                UtilitiesService.consoleLog(e);
            }
        }
        this.refreshSessionKey();

        // generate persistent key if it doesn't exist
        let tempPersistentKey = this.utilitiesService.getLocal('persistentKey');
        if (UtilitiesService.isEmpty(tempPersistentKey)) {
            this._persistentKey = UtilitiesService.randomString(24);
            this.utilitiesService.setLocal('persistentKey', this._persistentKey);
        } else {
            this._persistentKey = tempPersistentKey;
        }
        if (typeof window.dataLayer !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                'persistentKey': this._persistentKey
            });
        }

    } // constructor()

    get userDetails(): any {
        return this._userDetails;
    }

    set userDetails(value: any) {
        this._userDetails = value;
    }

    get prospectId(): number {
        return this._prospectId;
    }

    set prospectId(value: number) {
        this._prospectId = value;
    }

    // read only
    get persistentKey(): string {
        return this._persistentKey;
    }

    // read only
    get sessionKey(): string {
        if (UtilitiesService.isEmpty(this._sessionKey)) {
            return null;
        }
        return this._sessionKey.key;
    }

    refreshSessionKey() {
        let now = moment().format("YYYY-MM-DD HH:mm:ss");
        let newExpirationDate = moment().add(15, 'minutes').format("YYYY-MM-DD HH:mm:ss");

        try {
            if (UtilitiesService.isNotEmpty(this._sessionKey)) {
                if (now > this._sessionKey.expirationDate) {
                    let newSessionKey = UtilitiesService.randomString(24);
                    this._sessionKey = {key: newSessionKey, expirationDate: newExpirationDate};
                } else {
                    this._sessionKey.expirationDate = newExpirationDate;
                }
            } else {
                let newSessionKey = UtilitiesService.randomString(24);
                this._sessionKey = {key: newSessionKey, expirationDate: newExpirationDate};
            }
        } catch (error) {
            this._sessionKey = {};
        }
        this.utilitiesService.setLocal('sessionKey', JSON.stringify(this._sessionKey));

    } // refreshSessionKey()

    userEvent(params: any) {
        return this.http.post(
            this.authService.getBackendUrl("/user/clientevent"),
            JSON.stringify(params),
            this.authService.getBasicAuthHeaders()
        ).catch(() => {
            // we're ignoring errors on event calls
            // console.log(error);
            return of({});
        });

    } // userEvent()

    userEventAuthed(params: any) {
        return this.http.post(
            this.authService.getBackendUrl("/user/userevent"),
            JSON.stringify(params),
            this.authService.getAuthHeaders()
        ).catch(() => {
            // we're ignoring errors on event calls
            // console.log(error);
            return of({});
        });

    } // userEventAuthed()

    // missing parameters might be a mistake, log in dev
    private static checkEventValues(component: any, label: any, eventType: any, endpoint: any, endpointParams: any) {
        if (UtilitiesService.isEmpty(component)) {
            UtilitiesService.consoleLog('missing component event param: ' + component);
        }
        if (UtilitiesService.isEmpty(label)) {
            UtilitiesService.consoleLog('missing label event param: ' + label);
        }
        if (UtilitiesService.isEmpty(endpoint)) {
            UtilitiesService.consoleLog('missing endpoint event param: ' + endpoint);
        }
        if (endpointParams == null || typeof endpointParams == 'undefined') {
            UtilitiesService.consoleLog('missing endpointParams param: ', endpointParams);
        }
        if (!UtilitiesService.isEmpty(eventType) && typeof eventType === 'object') {
            if (!UtilitiesService.isEmpty(eventType.label) && !UtilitiesService.isEmpty(eventType.value)) {
                return true;
            } else {
                UtilitiesService.consoleLog('missing eventType.label or eventType.value event param eventType.label: ' + eventType.label + ', eventType.value: ' + eventType.value);
                return false;
            }
        }

        return false;

    } // checkEventValues()

    trackClickEvent(component: any, label: any, eventType: any) {
        if (EventTrackingService.checkEventValues(component, label, eventType, 'dummy value', 'dummy value')) {

            // log in ga
            this.angulartics2.eventTrack.next({
                action: eventType.label,
                properties: {
                    category: component,
                    label: label
                }
            });

            let trackParams = {
                category: 'click',
                type: eventType.value,
                name: eventType.label,
                component: component,
                ga: {action: eventType.label, properties: {category: component, label: label}}
            };
            this.trackEvent(trackParams);
        }

    } // trackClickEvent()

    trackSuccessEvent(component: any, label: any, eventType: any, endpoint: any, endpointParams: any, responseCode: number) {
        if (EventTrackingService.checkEventValues(component, label, eventType, endpoint, endpointParams)) {

            // log in ga
            this.angulartics2.eventTrack.next({
                action: eventType.label,
                properties: {
                    category: component,
                    label: label
                }
            });

            let trackParams = {
                category: 'success',
                type: eventType.value,
                name: eventType.label,
                component: component,
                ga: {action: eventType.label, properties: {category: component, label: label}},
                responseCode: responseCode,
                internal: {
                    endpoint: endpoint,
                    endpointParams: endpointParams,
                }
            };
            this.trackEvent(trackParams);
        }

    } // trackSuccessEvent()

    trackErrorEvent(component: any, label: any, eventType: any, endpoint: any, endpointParams: any, responseCode: number) {
        if (EventTrackingService.checkEventValues(component, label, eventType, endpoint, endpointParams)) {

            // log in ga
            this.angulartics2.eventTrack.next({
                action: eventType.label,
                properties: {
                    category: component,
                    label: label
                }
            });

            let trackParams = {
                category: 'error',
                type: eventType.value,
                name: eventType.label,
                component: component,
                ga: {action: eventType.label, properties: {category: component, label: label}},
                responseCode: responseCode,
                internal: {
                    endpoint: endpoint,
                    endpointParams: endpointParams,
                }
            };
            this.trackEvent(trackParams);
        }
    } // trackErrorEvent()

    trackEvent(params: any) {
        // set app version
        params.appVersion = environment.appVersion;

        // set page url
        if (UtilitiesService.isNotEmpty(this.router.url)) {
            params.pageUrl = this.router.url;
        }

        // set browser, browser version, os, etc from user data
        if (UtilitiesService.isNotEmpty(this._userDetails)) {
            for (let key in this._userDetails) {
                if (!this._userDetails.hasOwnProperty(key)) {
                    continue;
                }
                params[key] = this._userDetails[key];
            }
        }

        if (UtilitiesService.isNotEmpty(this.sessionKey)) {
            params.sessionKey = this.sessionKey;
        }

        if (UtilitiesService.isNotEmpty(this.persistentKey)) {
            params.persistentKey = this.persistentKey;
        }

        // details
        if (params.ga || params.internal) {
            let detail = JSON.stringify({
                ga: params.ga ? params.ga : null,
                internal: params.internal ? params.internal : null
            });

            try {
                params.detail = btoa(encodeURIComponent(detail).replace(/%([0-9A-F]{2})/g,
                    function toSolidBytes(match, p1) {
                        return String.fromCharCode(Number('0x' + p1));
                    }));
            } catch (e) {
                UtilitiesService.consoleLog(e);
            }

            // track ga (deprecated - universal analytics)
            if (params.ga) {
                this.angulartics2.eventTrack.next(params.ga);
            }

            delete params.ga;
            delete params.internal;
            delete params.test;
        }

        // authed or unauthed event call
        const userId = this.authService.getUserIdFromToken();
        if (UtilitiesService.isNotEmpty(userId)) {
            params.userId = userId;
            this.userEventAuthed(params).subscribe();
        } else {
            if (UtilitiesService.isNotEmpty(this.prospectId) && this.prospectId > 0) {
                params.prospectId = this.prospectId;
            } else if (UtilitiesService.isNotEmpty(this.newuserService.getProspectId())) {
                params.prospectId = this.newuserService.getProspectId();
            }
            this.userEvent(params).subscribe();
        }
    } // trackEvent()

    logConversion(isNewUser: boolean, isCustomQuote: boolean, ecommerce: any, value: any, component: string) {
        // new user
        if (isNewUser) {
            // custom quote
            if (isCustomQuote) {

                // google conversion
                this.angulartics2.eventTrack.next({
                    action: 'request quote',
                    properties: {
                        category: 'create account',
                        label: 'success'
                    }
                });

                // facebook tracking
                this.angulartics2.eventTrack.next({
                    action: 'Lead',
                    properties: {
                        value: '0.00',
                        currency: 'USD',
                        content_name: 'custom_quote',
                        content_category: 'quote'
                    }
                });

                // our conversion
                this.trackEvent({
                    category: 'success',
                    type: USER_EVENT_TYPE.NEW_USER_CUSTOM_QUOTE.value,
                    name: USER_EVENT_TYPE.NEW_USER_CUSTOM_QUOTE.label,
                    component: component
                });

                // booked job
            } else {

                // google conversion tracking
                this.angulartics2.eventTrack.next({
                    action: 'book appointment',
                    properties: {
                        category: 'new user payment',
                        label: 'success',
                        gtmCustom: {ecommerce: ecommerce}
                    }
                });

                // facebook tracking
                this.angulartics2.eventTrack.next({action: 'CompleteRegistration'});
                this.angulartics2.eventTrack.next({
                    action: 'Purchase',
                    properties: {
                        value: value,
                        currency: 'USD'
                    }});

                // our conversion tracking
                this.trackEvent({
                    category: 'success',
                    type: USER_EVENT_TYPE.NEW_USER_BOOKED_JOB.value,
                    name: USER_EVENT_TYPE.NEW_USER_BOOKED_JOB.label,
                    component: component
                });
            }

            // existing user
        } else {
            // custom quote
            if (isCustomQuote) {

                // google conversion
                this.angulartics2.eventTrack.next({
                    action: 'request quote',
                    properties: {
                        category: 'custom service',
                        label: 'success'
                    }
                });

                // facebook tracking
                this.angulartics2.eventTrack.next({
                    action: 'Lead',
                    properties: {
                        value: '0.00',
                        currency: 'USD',
                        content_name: 'custom_quote',
                        content_category: 'quote'
                    }
                });

                // our conversion
                this.trackEvent({
                    category: 'success',
                    type: USER_EVENT_TYPE.EXISTING_USER_CUSTOM_QUOTE.value,
                    name: USER_EVENT_TYPE.EXISTING_USER_CUSTOM_QUOTE.label,
                    component: component
                });

                // booked job
            } else {

                // google conversion tracking
                this.angulartics2.eventTrack.next({
                    action: 'book',
                    properties: {
                        category: 'review appointment',
                        label: 'success',
                        gtmCustom: {ecommerce: ecommerce}
                    }
                });

                // facebook tracking
                this.angulartics2.eventTrack.next({
                    action: 'Purchase',
                    properties: {
                        value: value,
                        currency: 'USD'
                    }});

                // our conversion tracking
                this.trackEvent({
                    category: 'success',
                    type: USER_EVENT_TYPE.EXISTING_USER_BOOKED_JOB.value,
                    name: USER_EVENT_TYPE.EXISTING_USER_BOOKED_JOB.label,
                    component: component
                });
            }
        }
    } // logConversion()

    logLead(loggedIn: boolean, ecommerce: any, total: number, component: string) {
        // google conversion tracking
        const action = loggedIn ? 'get authed quote' : 'get unauthed quote';
        this.angulartics2.eventTrack.next({
            action: action,
            properties: {
                category: 'cart',
                label: 'success'
            }
        });

        const contentName = loggedIn ? 'instant_quote' : 'instant_quote_newuser';
        // facebook tracking
        this.angulartics2.eventTrack.next({
            action: 'Lead',
            properties: {
                value: total,
                currency: 'USD',
                content_name: contentName,
                content_category: 'quote'
            }
        });

        const userEventType = loggedIn ? USER_EVENT_TYPE.EXISTING_USER_ESTIMATE : USER_EVENT_TYPE.NEW_USER_ESTIMATE;
        // our own tracking
        this.trackEvent({
            category: 'success',
            type: userEventType.value,
            name: userEventType.label,
            component: component
        });
    } // logLead()

    clickForm(type: any, component: any) {
        let userEventType = Object.assign({}, USER_EVENT_TYPE.CLICK_FORM);
        userEventType.label += ' - ' + type;
        this.trackClickEvent(component, 'click', userEventType);
    } // clickForm()

    /**
     * For appending a custom event string to an event type
     * @param eventType
     * @param customString
     */
    custom(eventType: any, customString: string) {
        const userEventType = Object.assign({}, eventType);
        userEventType.label += ' - ' + customString;
        return userEventType;
    } // custom()

    /* GA 4 Events */
    login() {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({event: "login"});
    }

    signup() {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({event: "sign_up"});
    }

    ecommerceSearch(searchTerm: string) {
        EventTrackingService.dataLayerPush({ ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "search",
            ecommerce: {
                search_term: searchTerm
            }
        });
    }

    ecommerceViewItemList() {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "view_item_list",
            ecommerce: {
                item_list_id: EventTrackingService.DEFAULT_ITEM_LIST_ID,
                item_list_name: EventTrackingService.DEFAULT_ITEM_LIST_NAME
            }
        });
    }

    ecommerceViewItem(service: any) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "view_item",
            ecommerce: {
                currency: "USD",
                value: service.total, // this isn't set
                items: [EventTrackingService.serviceToEcommerceItem(service)]
            }
        });
    }

    ecommerceSelectItem(service: any) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "view_item_list",
            ecommerce: {
                item_list_id: EventTrackingService.DEFAULT_ITEM_LIST_ID,
                item_list_name: EventTrackingService.DEFAULT_ITEM_LIST_NAME,
                items: [EventTrackingService.serviceToEcommerceItem(service)]
            }
        });
    }

    ecommerceAddItem(service: any) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "add_to_cart",
            ecommerce: {
                currency: "USD",
                value: service.total, // this isn't set
                items: [EventTrackingService.serviceToEcommerceItem(service)]
            }
        });
    }

    ecommerceLead(total: number) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "generate_lead",
            ecommerce: {
                currency: "USD",
                value: total
            }
        });
    }

    ecommerceBeginCheckout(services: any[], total: number, discountCode: string) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "begin_checkout",
            ecommerce: {
                currency: "USD",
                value: total,
                coupon: discountCode,
                items: services.map(EventTrackingService.serviceToEcommerceItem)
            }
        });
    }

    ecommerceRemoveItem(service: any) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "remove_from_cart",
            ecommerce: {
                currency: "USD",
                value: service.total,
                items: [EventTrackingService.serviceToEcommerceItem(service)]
            }
        });
    }

    ecommerceAddPaymentInfo(services: any, jobTotal: number, paymentType: number | string) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "add_payment_info",
            ecommerce: {
                currency: "USD",
                value: jobTotal,
                payment_type: EventTrackingService.getPaymentType(paymentType),
                items: services.map(EventTrackingService.serviceToEcommerceItem)
            }
        });
    }

    ecommerceAddShippingInfo(services: any, jobTotal: number) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "add_shipping_info",
            ecommerce: {
                currency: "USD",
                value: jobTotal,
                items: services.map(EventTrackingService.serviceToEcommerceItem)
            }
        });
    }

    ecommerceSelectTime() {
        // we don't clear the previous ecommerce object for this one - hope this is correct
        EventTrackingService.dataLayerPush({event: "selected_time"});
    }

    ecommercePurchase(job: any, quote: any) {
        const discountCode = job.discounts?.length > 0 ? job.discounts[0].code : "";
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "purchase",
            ecommerce: {
                currency: "USD",
                value: quote.total,
                transaction_id: job.id,
                coupon: discountCode,
                tax: quote.tax,
                items: job.services?.map(EventTrackingService.serviceToEcommerceItem)
            }
        });
    }

    ecommerceApplyPromo(discountCode: string, discountName: string) {
        EventTrackingService.dataLayerPush({ecommerce: null});  // Clear the previous ecommerce object.
        EventTrackingService.dataLayerPush({
            event: "select_promotion",
            ecommerce: {
                promotion_id: discountCode,
                promotion_name: discountName
            }
        });
    }

    private static dataLayerPush(json: any) {
        if (typeof window.dataLayer !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(json);
        }
    }

    private static getProductType(typeId: number): string {
        return Object.keys(PRODUCT_TYPE).find(key => PRODUCT_TYPE[key] === typeId);
    }

    private static getPaymentType(type: number | string): string {
        if (typeof(type) === "string") {
            return type;
        }
        return Object.keys(PAYMENT_TYPE).find(key => PAYMENT_TYPE[key] === type);
    }

    private static serviceToEcommerceItem(service: any): any {
        return {
            item_id: service.productId,
            item_name: service.name,
            discount: service.discount,
            item_category: EventTrackingService.getProductType(service.productType),
            item_list_id: EventTrackingService.DEFAULT_ITEM_LIST_ID,
            item_list_name: EventTrackingService.DEFAULT_ITEM_LIST_NAME,
            price: service.total,
            quantity: 1
        };
    }
}
